import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompanyStore } from '../../../stores/companyStore';
import { notify } from '../../../stores/notificationStore';
import {
  isVatExempt,
  formatCurrency,
  formatDate,
  formatVatRate,
} from '../../../utils/vatCalculations';
import { QuoteTemplateProps, QuoteItem } from '../types';
import { ActionBar } from '../shared/ActionBar';
import { UnifiedEmailModal, EmailData } from '../../modals/UnifiedEmailModal';
import { ItemsTable } from '../shared/ItemsTable';
import { publicLinksAPI, quotesAPI } from '../../../utils/api';
import { logger } from '../../../utils/logger';

/**
 * Template Corporate - Agency (STARTER) - DEVIS
 * Design ultra-minimaliste noir et blanc
 * Pour entreprises de services, style corporate et épuré
 */
export const QuoteTemplateCorporate: React.FC<QuoteTemplateProps> = ({
  quoteData,
  onClose,
  template,
  company: companyProp,
  color: colorProp
}) => {
  const { t } = useTranslation();
  const { company: companyStore } = useCompanyStore();
  const [isLoading, setIsLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [publicLink, setPublicLink] = useState<string>('');

  const company = companyProp || companyStore;
  const isExempt = isVatExempt(company?.vatRegime);

  if (!quoteData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 max-w-md text-center">
          <p className="text-red-600 font-semibold mb-4">{t('templates.quote.noData')}</p>
          <button onClick={onClose} className="px-4 py-2 bg-gradient-to-r from-teal-600 to-purple-600 hover:from-teal-700 hover:to-purple-700 text-white rounded shadow-lg shadow-teal-500/30 transition-all">
            {t('templates.common.back')}
          </button>
        </div>
      </div>
    );
  }

  // Normaliser les items : spread pour garder tous les champs (lineType, unit, descriptionType, technicalDescription...)
  // + override des numériques (Prisma Decimal → number, totalHT → total)
  const items: QuoteItem[] = typeof quoteData.items === 'string'
    ? JSON.parse(quoteData.items).map((item: any) => {
        const unitPrice = Number(item.unitPrice) || 0;
        const quantity = Number(item.quantity) || 0;
        return {
          ...item,
          unitPrice,
          quantity,
          total: Number(item.totalHT) || Number(item.total) || (unitPrice * quantity),
          description: item.description || '',
          vatRate: item.taxPercent || (isExempt ? 0 : 20),
          vatAmount: item.vatAmount || 0,
        };
      })
    : (quoteData.items || []).map((item: any) => {
        const unitPrice = Number(item.unitPrice) || 0;
        const quantity = Number(item.quantity) || 0;
        return {
          ...item,
          unitPrice,
          quantity,
          total: Number(item.totalHT) || Number(item.total) || (unitPrice * quantity),
          description: item.description || '',
          vatRate: item.taxPercent || (isExempt ? 0 : 20),
          vatAmount: item.vatAmount || 0,
        };
      });

  // ✅ Construire la ventilation TVA à partir des items (affichage uniquement)
  const vatBreakdown: Array<{ rate: number; baseHT: number; vatAmount: number }> = [];
  const vatByRate: Record<number, { baseHT: number; vatAmount: number }> = {};

  items.forEach((item: QuoteItem) => {
    const rate = item.taxPercent || 0;
    if (!vatByRate[rate]) {
      vatByRate[rate] = { baseHT: 0, vatAmount: 0 };
    }
    vatByRate[rate].baseHT += item.total || 0;
    const vatAmt = (item.total || 0) * (rate / 100);
    vatByRate[rate].vatAmount += vatAmt;
  });

  Object.entries(vatByRate).forEach(([rate, data]) => {
    vatBreakdown.push({
      rate: Number(rate),
      baseHT: data.baseHT,
      vatAmount: data.vatAmount
    });
  });

  // ✅ Utiliser les valeurs du backend (source de vérité)
  const subtotal = parseFloat(String(quoteData.totalHT)) || parseFloat(String(quoteData.subtotal)) || 0;
  const tax = parseFloat(String(quoteData.totalTVA)) || parseFloat(String(quoteData.tax)) || 0;
  const total = parseFloat(String(quoteData.totalTTC)) || parseFloat(String(quoteData.total)) || 0;

  const handleGeneratePDF = async () => {
    if (!quoteData.id) {
      notify.error('ID du devis manquant', 'Génération PDF');
      return;
    }
    setIsLoading(true);
    try {
      // ✅ Utilisation du backend pour générer le PDF (templates uniformisés)
      const blob = await quotesAPI.downloadPDF(quoteData.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const clientName = (quoteData as any).Client?.fullName || (quoteData as any).Client?.companyName || 'Client';
      const quoteNumber = (quoteData.quoteNumber || 'Devis').replace(/^DEVIS-/i, '');
      a.download = `Devis_${quoteNumber}_${clientName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notify.success('PDF généré avec succès', 'Génération PDF');
    } catch (error) {
      logger.error('Erreur génération PDF:', error);
      notify.error("Erreur lors de la génération du PDF : " + (error as Error).message, 'Génération PDF');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEmailModal = async () => {
    try {
      setIsLoading(true);
      if (!quoteData.id) {
        throw new Error('ID du devis manquant');
      }

      // 1. Essayer de récupérer un lien existant
      try {
        const result = await publicLinksAPI.getQuoteLink(quoteData.id);
        if (result.success && result.data && result.data.length > 0) {
          const link = result.data[0];
          setPublicLink(`${window.location.origin}/q/${link.token}`);
          return;
        }
      } catch {
        // 404 ou erreur réseau — on continue pour créer un lien
      }

      // 2. Pas de lien existant, en créer un nouveau
      const createResult = await publicLinksAPI.createQuoteLink(quoteData.id);
      if (createResult.success && createResult.data && createResult.data.token) {
        setPublicLink(`${window.location.origin}/q/${createResult.data.token}`);
      }
    } catch (error) {
      logger.error('Erreur lors de la récupération du lien public:', error);
    } finally {
      setIsLoading(false);
      setShowEmailModal(true);
    }
  };

  const handleSendEmail = async (emailData: EmailData) => {
    try {
      if (!quoteData?.id) {
        throw new Error('ID du devis manquant');
      }

      setIsLoading(true);

      // Envoyer en JSON — le backend génère le PDF côté serveur
      await publicLinksAPI.sendQuoteEmail(quoteData.id, {
        to: emailData.to,
        subject: emailData.subject,
        body: emailData.body,
      });

      notify.success(`Email envoyé à ${emailData.to}`, 'Envoi réussi');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue';
      notify.error(message, 'Erreur d\'envoi');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen print:min-h-0 print:bg-white">
      <ActionBar
        onGeneratePDF={handleGeneratePDF}
        onGenerateFacturX={handleGeneratePDF}
        onSendEmail={handleOpenEmailModal}
        onPrint={handlePrint}
        onClose={onClose}
        isLoading={isLoading}
        documentType="quote"
      />

      <UnifiedEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={handleSendEmail}
        documentType="quote"
        documentNumber={quoteData.quoteNumber}
        clientEmail={quoteData.clientEmail || ''}
        clientName={quoteData.clientName || ''}
        showPdfPreview={false}
        templateVariables={{
          quoteNumber: quoteData.quoteNumber || '',
          clientName: quoteData.clientName || '',
          total: total.toFixed(2),
          publicLink: publicLink,
          validUntil: formatDate(quoteData.validUntil),
          companyName: company?.legalName || company?.tradeName || '',
          companyEmail: company?.email || '',
          companyPhone: company?.phone || '',
        }}
      />

      <div className="py-8 print:py-0 print:mt-0">
        <div className="max-w-4xl mx-auto bg-white shadow-lg print:shadow-none print:max-w-full overflow-hidden" id="quote-template">
          <div className="p-12 print:p-8">

            {/* HEADER - "Devis" + Infos + Logo */}
            <div className="flex justify-between items-start mb-8 pb-8">
              <div>
                <h1 className="text-4xl font-bold text-black mb-6">{t('templates.quote.titleLower')}</h1>
                <div className="text-sm leading-loose text-gray-800">
                  <p className="mb-2">
                    <span className="font-semibold">N° :</span> {quoteData.quoteNumber?.replace(/^DEVIS-/i, '')}
                  </p>
                  <p className="mb-2">
                    <span className="font-semibold">{t('templates.invoice.issueDateLabel')}</span> {formatDate(quoteData.date || quoteData.issueDate)}
                  </p>
                  <p>
                    <span className="font-semibold">{t('templates.quote.validUntilLabel')}</span> {formatDate(quoteData.validUntil)}
                  </p>
                </div>
              </div>

              {/* Logo à droite */}
              <div className="text-right">
                {company?.logoUrl && (
                  <img
                    src={company.logoUrl}
                    alt={company.legalName || company.tradeName || 'Logo'}
                    className="h-20 max-w-[200px] object-contain ml-auto"
                  />
                )}
              </div>
            </div>

            {/* ADRESSES - 2 colonnes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-8 pb-8 border-b border-gray-300">
              {/* Émetteur */}
              <div className="text-sm leading-relaxed text-gray-800">
                <div className="font-semibold text-[15px] mb-2 text-black">
                  {company?.legalName || company?.tradeName}
                </div>
                {company?.addressLine1 && <p className="mb-1">{company.addressLine1}</p>}
                {(company?.zipCode || company?.city) && (
                  <p className="mb-1">
                    {company?.zipCode} {company?.city}, {company?.taxCountryCode || 'FR'}
                  </p>
                )}
                {company?.email && <p className="mb-1">{company.email}</p>}
              </div>

              {/* Client */}
              <div className="text-sm leading-relaxed text-gray-800">
                <div className="font-semibold text-[15px] mb-2 text-black">
                  {quoteData.clientCompany || quoteData.clientName}
                </div>
                {quoteData.clientAddressLine1 && <p className="mb-1">{quoteData.clientAddressLine1}</p>}
                {(quoteData.clientZipCode || quoteData.clientCity) && (
                  <p className="mb-1">
                    {quoteData.clientZipCode} {quoteData.clientCity}, FR
                  </p>
                )}
                {quoteData.clientEmail && <p className="mb-1">{quoteData.clientEmail}</p>}
              </div>
            </div>

            {/* TITRE DU DOCUMENT */}
            {quoteData.title && (
              <div className="mb-8 pb-4 border-b border-gray-300 text-2xl font-normal tracking-wide text-gray-800">
                {quoteData.title}
              </div>
            )}

            {/* TABLEAU DES ITEMS — délégué à ItemsTable (sections, remises, descriptions BTP) */}
            <ItemsTable
              items={items}
              tableStyle="bordered"
              accentColor="#111827"
              showVat={!isExempt}
            />

          </div>

          {/* TOTAUX */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 px-12 py-8 bg-white border-t border-gray-400">
            <div></div>
            <div className="ml-auto w-full">
              <div className="flex justify-between py-2 text-[15px]">
                <span className="text-gray-700">{t('templates.common.totalHT')}</span>
                <span className="font-semibold text-black">{formatCurrency(subtotal)}</span>
              </div>

              {/* TVA - ventilation uniquement si plusieurs taux */}
              {!isExempt && (
                <>
                  {vatBreakdown.length > 1 ? (
                    /* Ventilation TVA si plusieurs taux */
                    vatBreakdown.map((vat, index) => (
                      <div key={index} className="flex justify-between py-2 text-[15px]">
                        <span className="text-gray-700">TVA {formatVatRate(vat.rate)}</span>
                        <span className="font-semibold text-black">{formatCurrency(vat.vatAmount)}</span>
                      </div>
                    ))
                  ) : tax > 0 && (
                    /* TVA simple si un seul taux */
                    <div className="flex justify-between py-2 text-[15px]">
                      <span className="text-gray-700">TVA {vatBreakdown.length === 1 ? formatVatRate(vatBreakdown[0].rate) : ''}</span>
                      <span className="font-semibold text-black">{formatCurrency(tax)}</span>
                    </div>
                  )}
                </>
              )}

              {/* Total TTC - fond gris foncé */}
              <div className="flex justify-between bg-gray-300 px-3 py-3 mt-2 font-bold text-base">
                <span className="text-black">{t('templates.common.totalTTC')}</span>
                <span className="text-black">{formatCurrency(total)}</span>
              </div>
            </div>
          </div>

          {/* CONDITIONS - Fond gris */}
          <div className="px-12 py-8 bg-gray-200 text-xs text-gray-600 leading-relaxed border-t border-gray-400">
            <p><strong className="text-gray-900">{t('templates.common.validityLabel')}</strong> {t('templates.common.validityText', { date: formatDate(quoteData.validUntil) })}</p>
            <p>{t('templates.common.noDiscountForEarlyPayment')}</p>
            {company?.websiteUrl && (
              <p className="mt-3"><a href={company.websiteUrl} className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">{company.websiteUrl.replace(/^https?:\/\//, '')}</a></p>
            )}
          </div>

          {/* Conditions Générales de Vente */}
          {(company as any)?.legalNotesDefault && (
            <div className="px-12 py-8 bg-gray-50 border-t border-gray-200">
              <h3 className="text-sm font-bold text-gray-700 mb-3">{t('templates.common.cgv')}</h3>
              <div className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed">
                {(company as any).legalNotesDefault}
              </div>
            </div>
          )}

          {/* Notes / Conditions générales */}
          {quoteData.termsConditions && (
            <div className="px-12 py-8 bg-gray-50 border-t border-gray-200">
              <h3 className="text-sm font-bold text-gray-700 mb-3">{t('templates.common.cgvShort')}</h3>
              <p className="text-xs text-gray-600 whitespace-pre-wrap">{quoteData.termsConditions}</p>
            </div>
          )}

          {/* Informations légales uniquement */}
          {(company?.registrationNumber || company?.vatNumber || company?.legalForm || company?.capital || company?.rcsNumber || company?.apeCode) && (
            <div className="px-12 py-6 bg-white border-t border-gray-200">
              <p className="text-xs text-gray-600 text-center">
                {[
                  company?.registrationNumber && `SIRET: ${company.registrationNumber}`,
                  company?.vatNumber && `TVA: ${company.vatNumber}`,
                  company?.legalForm,
                  company?.capital && `Capital: ${company.capital}€`,
                  (company?.rcsNumber && company?.rcsCity) && `RCS ${company.rcsCity} ${company.rcsNumber}`,
                  company?.apeCode && `APE: ${company.apeCode}`
                ].filter(Boolean).join(' • ')}
              </p>
            </div>
          )}

          {/* Footer - fond gris */}
          <div className="flex justify-between items-center text-xs text-gray-800 px-12 py-4 pb-8 print:pb-16 bg-gray-200 border-t border-gray-400">
            <div>
              {(company?.legalName || company?.tradeName) && <p>{company?.legalName || company?.tradeName}, {company?.legalForm || 'SAS'}</p>}
            </div>
            <div className="text-right">
              <p>{quoteData.quoteNumber} · 1/1</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};
